/**
 * DemoDataOverlay — "View Example Data" Toggle + Banner (V108)
 *
 * Shows a clearly marked demo-mode banner when active.
 * Toggle button available in all workspace headers.
 *
 * Props:
 *   workspace   — "sme" | "corporate" | "project" | "fi" | "states"
 *   demoMode    — boolean (from useDemoMode)
 *   onToggle    — function to toggle
 *
 * Isolation rules:
 *   - demo data is purely static (no API calls)
 *   - toggle OFF returns to real data
 *   - no persistence
 *   - no mixing with real data
 */
import React from "react";
import { Eye, EyeOff, Beaker } from "lucide-react";

// ── DemoToggle — compact button for workspace header ─────────────────────────
export function DemoToggle({ workspace, demoMode, onToggle }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-bold transition-all ${
        demoMode
          ? "bg-amber-100 text-amber-700 border border-amber-300 ring-1 ring-amber-300"
          : "bg-white/10 text-white/60 border border-white/20 hover:bg-white/20 hover:text-white/80"
      }`}
      title={demoMode ? "Exit example data view" : "View example data"}
      data-testid={`demo-toggle-${workspace}`}>
      {demoMode ? <EyeOff size={11} /> : <Eye size={11} />}
      {demoMode ? "Exit Demo" : "View Example Data"}
    </button>
  );
}

// ── DemoBanner — persistent banner when demo mode is active ───────────────────
export function DemoBanner({ workspace, onExit }) {
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-center justify-between mb-4"
      data-testid="demo-banner">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-amber-200 flex items-center justify-center shrink-0">
          <Beaker size={12} className="text-amber-700" />
        </div>
        <div>
          <p className="text-[9px] font-bold text-amber-800 uppercase tracking-wider">
            Example Data — Read Only
          </p>
          <p className="text-[8px] text-amber-600">
            You are viewing a demo dataset. This data is not your real data.
            No calculations, reports, or saves will use this data.
          </p>
        </div>
      </div>
      <button onClick={onExit}
        className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded-xl hover:bg-amber-200 border border-amber-300 shrink-0"
        data-testid="demo-exit-btn">
        <EyeOff size={11} /> Exit Demo
      </button>
    </div>
  );
}

// ── DemoSection — wraps individual demo content blocks ────────────────────────
export function DemoSection({ title, children }) {
  return (
    <div className="relative">
      <div className="absolute top-2 right-2 z-10">
        <span className="text-[7px] font-bold px-1.5 py-0.5 bg-amber-100 text-amber-600 border border-amber-200 rounded-full uppercase tracking-wider">
          Demo
        </span>
      </div>
      {children}
    </div>
  );
}

// ── DemoDataDisplay — renders demo snapshot for each workspace ─────────────────
export function DemoDataDisplay({ workspace }) {
  const { DEMO_DATA } = require("@/demo/demoSnapshots");
  const data = DEMO_DATA[workspace];
  if (!data) return null;

  if (workspace === "sme") return <SMEDemoDisplay data={data} />;
  if (workspace === "corporate") return <CorporateDemoDisplay data={data} />;
  if (workspace === "project") return <ProjectDemoDisplay data={data} />;
  if (workspace === "fi") return <FIDemoDisplay data={data} />;
  if (workspace === "states") return <StatesDemoDisplay data={data} />;
  return null;
}

function SMEDemoDisplay({ data }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-amber-100 rounded-xl p-5">
        <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider mb-2">
          {data.org_name} · {data.sector} · {data.state}
        </p>
        <p className="text-[8px] text-slate-400 mb-3 italic">{data.description}</p>
        <div className="grid grid-cols-3 gap-4 mb-3">
          <div className="bg-teal-50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-teal-700">{data.ytd_tco2e}</p>
            <p className="text-[7px] text-slate-400">YTD tCO2e</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-lg font-bold text-blue-700">{data.confidence_avg}</p>
            <p className="text-[7px] text-slate-400">Avg Confidence</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <p className="text-sm font-bold text-purple-700">{data.financing_status}</p>
            <p className="text-[7px] text-slate-400">Financing Status</p>
          </div>
        </div>
        <div className="space-y-1">
          {data.periods.map(p => (
            <div key={p.period_label} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
              <span className="text-[8px] font-mono text-slate-500 w-14">{p.period_label}</span>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-teal-400 rounded-full" style={{ width: `${Math.min(p.total_tco2e / 8 * 100, 100)}%` }} />
              </div>
              <span className="text-[8px] font-bold text-teal-700 w-12 text-right">{p.total_tco2e.toFixed(2)}</span>
              {p.is_locked && <span className="text-[7px] text-emerald-600">🔒</span>}
            </div>
          ))}
        </div>
        <p className="text-[8px] text-slate-500 italic mt-3 border-t border-slate-50 pt-2">{data.key_insight}</p>
      </div>
    </div>
  );
}

function CorporateDemoDisplay({ data }) {
  return (
    <div className="space-y-4">
      <div className="bg-white border border-amber-100 rounded-xl p-5">
        <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider mb-2">{data.org_name} · {data.sector}</p>
        <p className="text-[8px] text-slate-400 mb-3 italic">{data.description}</p>
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="bg-amber-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-amber-700">{(data.ytd_tco2e/1000).toFixed(0)}kt</p>
            <p className="text-[7px] text-slate-400">YTD tCO2e</p>
          </div>
          <div className="bg-teal-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-teal-700">{data.intensity.tco2e_per_fte}</p>
            <p className="text-[7px] text-slate-400">tCO2e/FTE</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-2 text-center">
            <p className="text-base font-bold text-blue-700">{data.portfolio_score}</p>
            <p className="text-[7px] text-slate-400">Portfolio Score</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-2 text-center">
            <p className="text-sm font-bold text-purple-700">{data.monetisation_opportunities.length}</p>
            <p className="text-[7px] text-slate-400">Opportunities</p>
          </div>
        </div>
        <p className="text-[8px] font-bold text-slate-500 mb-1">Top Monetisation Opportunities:</p>
        {data.monetisation_opportunities.map((o, i) => (
          <div key={i} className="flex items-center gap-2 py-1 border-b border-slate-50 last:border-0">
            <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded-full ${o.priority === 'high' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{o.priority}</span>
            <p className="text-[8px] text-slate-600 flex-1">{o.label}</p>
            <p className="text-[8px] font-bold text-teal-700">{o.annual_reduction.toFixed(0)} tCO2e/yr</p>
          </div>
        ))}
        <p className="text-[8px] text-slate-500 italic mt-2">{data.key_insight}</p>
      </div>
    </div>
  );
}

function ProjectDemoDisplay({ data }) {
  return (
    <div className="bg-white border border-amber-100 rounded-xl p-5">
      <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider mb-1">{data.project_name}</p>
      <p className="text-[8px] text-slate-400 mb-3 italic">{data.description}</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-teal-50 rounded-xl p-2 text-center">
          <p className="text-base font-bold text-teal-700">{data.annual_reduction}</p>
          <p className="text-[7px] text-slate-400">tCO2e/yr reduction</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-2 text-center">
          <p className="text-base font-bold text-blue-700">{data.eligibility_score}/100</p>
          <p className="text-[7px] text-slate-400">Eligibility Score</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-2 text-center">
          <p className="text-sm font-bold text-emerald-700">{data.emissions_reduction_pct}%</p>
          <p className="text-[7px] text-slate-400">Baseline reduction</p>
        </div>
      </div>
      <div className="bg-amber-50 rounded-xl p-2 mb-2">
        <p className="text-[8px] font-bold text-amber-700">Carbon Revenue: {data.carbon_revenue_range.low.toLocaleString()}–{data.carbon_revenue_range.high.toLocaleString()} {data.carbon_revenue_range.currency}/yr</p>
        <p className="text-[7px] text-slate-400">{data.carbon_revenue_range.basis}</p>
      </div>
      <p className="text-[8px] text-slate-500 italic">{data.key_insight}</p>
    </div>
  );
}

function FIDemoDisplay({ data }) {
  return (
    <div className="space-y-3">
      <p className="text-[8px] text-slate-400 italic">{data.description}</p>
      {data.deals.map(d => (
        <div key={d.deal_id} className="bg-white border border-amber-100 rounded-xl p-4">
          <div className="flex items-start justify-between mb-1">
            <p className="text-xs font-bold text-[#0F172A]">{d.entity}</p>
            <span className={`text-[7px] font-bold px-2 py-0.5 rounded-full ${
              d.status === 'term_sheet' ? 'bg-emerald-100 text-emerald-700' :
              d.status === 'credit_committee' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
            }`}>{d.status.replace(/_/g,' ').toUpperCase()}</span>
          </div>
          <p className="text-[8px] text-teal-700 font-medium">{d.est_annual_carbon_revenue}/yr est.</p>
          <p className="text-[8px] text-slate-400 mt-0.5">{d.notes}</p>
        </div>
      ))}
      <div className="bg-teal-50 border border-teal-200 rounded-xl px-3 py-2">
        <p className="text-[8px] font-bold text-teal-700">{data.pipeline_summary.total_reduction_tco2e} combined reduction potential | {data.pipeline_summary.total_capex_pipeline} pipeline</p>
      </div>
    </div>
  );
}

function StatesDemoDisplay({ data }) {
  return (
    <div className="bg-white border border-amber-100 rounded-xl p-5">
      <p className="text-[9px] font-bold text-amber-600 uppercase tracking-wider mb-1">{data.state_name} — {data.iso_code}</p>
      <p className="text-[8px] text-slate-400 mb-3 italic">{data.description}</p>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-teal-50 rounded-xl p-2 text-center">
          <p className="text-base font-bold text-teal-700">{data.aggregation.org_count}</p>
          <p className="text-[7px] text-slate-400">Organizations</p>
        </div>
        <div className="bg-amber-50 rounded-xl p-2 text-center">
          <p className="text-base font-bold text-amber-700">{data.aggregation.total_footprint_tco2e}</p>
          <p className="text-[7px] text-slate-400">tCO2e Footprint</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-2 text-center">
          <p className="text-base font-bold text-emerald-700">{(data.aggregation.total_reduction_potential/1000).toFixed(1)}kt</p>
          <p className="text-[7px] text-slate-400">Reduction Potential</p>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-2">
        <p className="text-[8px] font-bold text-blue-700">{data.programme.name}</p>
        <p className="text-[8px] text-slate-500">{data.programme.type.toUpperCase()} · {data.programme.projects} projects · {data.programme.status} → FI: {data.programme.fi_deal}</p>
      </div>
      <p className="text-[8px] text-slate-500 italic">{data.key_insight}</p>
    </div>
  );
}
